import { Button, Checkbox, Modal, ModalActions, ModalContent, ModalTitle } from "@kaltura/ds-react-components";
import {
    CreationToolInjectedProps,
    PreCreationToolProps,
    SearchAndSelect,
    SearchAndSelectColumnId,
    SearchAndSelectSource,
} from "@kaltura/mediaspace-shared-search-and-select";
import { postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { AlertColor, styled } from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import { FormLabel } from "@kaltura/mediaspace-shared-ui/form";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType, SearchAndSelectEntry } from "@kaltura/mediaspace-shared-types";
import { CreationToolWrapper } from "./creation-tools/creation-tool-wrapper/CreationToolWrapper";
import { missingCaptionsPredicate, missingRequiredFieldsPredicate } from "@kaltura/mediaspace-shared-search-and-select";
import { useEventHandler } from "@kaltura/mediaspace-shared-hooks";

export interface AddToCategoryProps {
    /**
     * what's the category type: channel or gallery
     */
    isChannel: boolean;

    /**
     * show modal
     */
    open: boolean;

    /**
     * modal close button callback
     */
    onClose: () => void;

    /**
     * data sources
     */
    tabs: SearchAndSelectSource[];

    /**
     * table columns
     */
    columns: SearchAndSelectColumnId[];

    /**
     * table page size
     */
    pageSize: number;

    /**
     * url for data submission.
     * target category id is expected to be part of the url.
     */
    submitUrl: string;

    /**
     * url of (possibly external) Terms-Of-Use page
     */
    touLink?: string;
}

const StyledModal = styled(
    Modal,
    { shouldForwardProp: (propName) => propName !== "showCreationTool" }
)<{ showCreationTool: boolean }>(({ theme, showCreationTool }) => ({
    "& .kms-ds-add-to-category-modal": {
        maxWidth: 1200,
        width: showCreationTool ? "auto" : 1200,
        maxHeight: "85vh",
        [theme.breakpoints.down("lg")]: {
            maxWidth: 800,
            width: showCreationTool ? "auto" : 800,
        },
        [theme.breakpoints.down("md")]: {
            maxWidth: 552,
            width: 552,
        },
    },
}));

const StyledModalContent = styled(ModalContent)(({ theme }) => ({
    "&.kms-ds-add-to-category-modal-content": {
        [theme.breakpoints.down("md")]: {
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
        },
    },
}));

const StyledModalActions = styled(
    ModalActions,
    {
        shouldForwardProp: (propName) => propName !== "hasTou",
    }
)<{ hasTou: boolean }>(({ theme, hasTou }) => ({
    ...(
        hasTou && {
            justifyContent: "space-between",
            alignItems: "flex-end",
        }
    ),
    [theme.breakpoints.down("md")]: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
}));

const StyledInnerActionsContainer = styled("div")(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(1),
}));

/**
 * AddToCategory modal component
 */
export function AddToCategory({
    isChannel,
    open,
    onClose,
    tabs,
    columns,
    pageSize,
    submitUrl,
    touLink,
}: AddToCategoryProps) {
    // Category type, to be used for debug messages
    const type = isChannel ? "channel" : "gallery";

    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [createdItems, setCreatedItems] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [touApproved, setTouApproved] = useState(!touLink);
    const [creationTool, setCreationTool] = useState<KmsTypeComponent<any, CreationToolInjectedProps>>();
    const [preCreationTool, setPreCreationTool] = useState<KmsTypeComponent<any, PreCreationToolProps>>();

    /**
     * when selecting items from a category (shared repo / channel / gallery), the category id. for my-media it will be 0.
     */
    const [selectionSourceId, setSelectionSourceId] = useState<number>(0);

    const { showToast } = useToastsContext();

    const sendButtonAnalytics = useButtonAnalytics();

    const conditionalMessages = useMemo(
        () => [
            {
                // missing mandatory fields
                message: translate(
                    "Some items are unavailable. All mandatory fields must be filled before media can be published."
                ),
                severity: "warning" as AlertColor,
                predicate: missingRequiredFieldsPredicate,
            },
            {
                // missing captions
                message: translate(
                    "Some items are unavailable. Captions must be available before media can be published."
                ),
                severity: "warning" as AlertColor,
                predicate: missingCaptionsPredicate,
            },
            {
                message: translate("Some media items are already published to this location."),
                severity: "info" as AlertColor,
                predicate: (entry: SearchAndSelectEntry) => entry.disabledChecked || false,
            },
        ],
        []
    );

    const cancelHandler = () => {
        // reset
        setLoading(false);
        setSelectedItems([]);
        setTouApproved(!touLink);
        // notify wrapper
        onClose();
        // send analytics
        sendButtonAnalytics(`Add to ${type} - cancel`, ButtonClickAnalyticsType.CLOSE);
    };

    const submitHandler = async () => {
        const reset = () => {
            setLoading(false);
            setSelectedItems([]);
            setTouApproved(!touLink);
        };

        sendButtonAnalytics(`Add to ${type} - submit`, ButtonClickAnalyticsType.ADD);

        setLoading(true);
        const tab = tabs.find((tab) => tab.id === selectionSourceId);

        const data = {
            entryIds: selectedItems,
            ...(tab && tab.sourceType === "sharedRepository" ? { sharedRepoId: selectionSourceId } : {}),
        };

        try {
            const response: AddToCategorySubmitResponse = await postKmsData(submitUrl, data);

            // notify user
            if (response.success) {
                console.log("Media added to category successfully", response.messages);
                onClose();
                // toast messages will show on page after refresh
                window.location.reload();
            }
            else {
                reset();
                console.log(`${submitUrl} failed`, response.messages);
                if (response.messages && response.messages.length > 0) {
                    const msg = `<ul><li>${response.messages.join("</li><li>")}</li></ul>`;
                    showToast({
                        message: msg,
                        severity: "warning",
                    });
                }
                onClose();
            }
        }
        catch (e) {
            console.debug(`${submitUrl} failed`, e);
            showToast({
                message: "failed.",
                severity: "error",
            });
            reset();
            onClose();
        }
    };
    const handleSubmit = useEventHandler(submitHandler);

    const handleSelectionChange = (selection: string[], sourceCategoryId: number) => {
        setSelectedItems(selection);
        setSelectionSourceId(sourceCategoryId);
    };

    const handleCreationToolSelected = (
        component: KmsTypeComponent<any, CreationToolInjectedProps>,
        preComponent?: KmsTypeComponent<any, PreCreationToolProps>
    ) => {
        setCreationTool(component);
        setPreCreationTool(preComponent);
    };

    const creationToolSubmitHandler = (entryIds: string[]) => {
        setSelectedItems(entryIds);
        setCreatedItems(entryIds);
    };

    useEffect(() => {
        createdItems.length > 0 && handleSubmit();
    }, [createdItems, handleSubmit]);

    return (
        <StyledModal
            open={open}
            showCreationTool={!!creationTool}
            variant={"task"}
            onClose={(_, reason) => {
                if (reason !== "backdropClick") {
                    onClose();
                }
            }}
            classes={{ paper: "kms-ds-add-to-category-modal" }}
        >
            {!creationTool && <ModalTitle variant={"h2"}>{translate("Select media")}</ModalTitle>}
            <StyledModalContent
                sx={{ display: creationTool ? "none" : undefined }}
                className={"kms-ds-add-to-category-modal-content"}
            >
                <SearchAndSelect
                    tabs={tabs}
                    columns={columns}
                    selectionMode={"multiple"}
                    onSelectionChange={handleSelectionChange}
                    pageSize={pageSize}
                    showExploreButton={false}
                    conditionalMessages={conditionalMessages}
                    onCreationToolSelected={handleCreationToolSelected}
                />
            </StyledModalContent>
            {!creationTool && (
                <StyledModalActions hasTou={!!touLink}>
                    {touLink && (
                        <StyledInnerActionsContainer>
                            <Checkbox
                                id={"touCheckbox"}
                                value={"nothing"}
                                onChange={(event) => {
                                    setTouApproved(event.currentTarget.checked);
                                }}
                            />
                            <FormLabel
                                htmlFor={"touCheckbox"}
                                text={translate(
                                    "I accept the %1Terms of Use%2",
                                    [`<a href="${touLink}" target="blank">`, "</a>"]
                                )}
                                id={"startTimeLabel"}
                            />
                        </StyledInnerActionsContainer>
                    )}

                    <StyledInnerActionsContainer>
                        <Button variant={"borderless"} color={"secondary"} onClick={cancelHandler}>
                            {translate("Cancel")}
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={selectedItems.length === 0 || loading || !touApproved}
                            loading={loading}
                        >
                            {translate("Select")}
                        </Button>
                    </StyledInnerActionsContainer>
                </StyledModalActions>
            )}

            {creationTool && (
                <CreationToolWrapper
                    creationTool={creationTool}
                    preCreationToolComponent={preCreationTool}
                    onBack={() => setCreationTool(undefined)}
                    onCancel={cancelHandler}
                    onSubmit={creationToolSubmitHandler}
                />
            )}
        </StyledModal>
    );
}

export type AddToCategorySubmitResponse = {
    /** was the call successful, determines toast color */
    success: boolean;
    /** list of messages (translated tests) if any */
    messages?: string[];
};

export default AddToCategory;
